import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getTutorCertificationsOptions,
} from '../../api/generated/@tanstack/react-query.gen';
import {
  createTutorCertification,
  updateTutorCertification,
  deleteTutorCertification,
} from '../../api/generated/sdk.gen';
import type {
  TutorCertification,
  TutorCertificationInput,
} from '../../api/generated/types.gen';

// Re-export generated type
export type { TutorCertification };

// Params type for backward compatibility with existing code
export interface CreateCertificationParams {
  type: string;
  custom_title?: string;
  institution?: string;
  year_from: number | null;
  year_to: number | null;
}

export interface UpdateCertificationParams extends CreateCertificationParams {
  id: string;
}

/**
 * Query hook for fetching a specific tutor's certifications (for viewing another tutor's profile)
 */
export function useTutorCertificationsQuery(userId: string | undefined) {
  return useQuery({
    ...getTutorCertificationsOptions({ path: { userId: userId! } }),
    select: (data) => data.certifications,
    enabled: !!userId,
  });
}

/**
 * Query hook for fetching the current user's certifications
 * Uses the same endpoint but with a different cache key pattern for "my" data
 */
export function useMyCertificationsQuery(userId: string | undefined) {
  return useQuery({
    ...getTutorCertificationsOptions({ path: { userId: userId! } }),
    select: (data) => data.certifications,
    enabled: !!userId,
  });
}

/**
 * Mutation hook for creating a new certification entry
 * Adapts the simple params interface to the generated SDK format
 */
export function useCreateCertificationMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: CreateCertificationParams) => {
      const { data } = await createTutorCertification({
        body: { tutors_certification: params as TutorCertificationInput },
        throwOnError: true,
      });
      return data;
    },
    onSuccess: () => {
      // Invalidate all tutor certification queries to refetch fresh data
      queryClient.invalidateQueries({ queryKey: [{ _id: 'getTutorCertifications' }] });
    },
  });
}

/**
 * Mutation hook for updating an existing certification entry
 * Adapts the simple params interface to the generated SDK format
 */
export function useUpdateCertificationMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: UpdateCertificationParams) => {
      const { id, ...rest } = params;
      const { data } = await updateTutorCertification({
        body: { tutors_certification: rest as TutorCertificationInput },
        path: { id },
        throwOnError: true,
      });
      return data;
    },
    onSuccess: () => {
      // Invalidate all tutor certification queries to refetch fresh data
      queryClient.invalidateQueries({ queryKey: [{ _id: 'getTutorCertifications' }] });
    },
  });
}

/**
 * Mutation hook for deleting a certification entry
 * Adapts the simple id param to the generated SDK format
 */
export function useDeleteCertificationMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await deleteTutorCertification({
        path: { id },
        throwOnError: true,
      });
      return data;
    },
    onSuccess: () => {
      // Invalidate all tutor certification queries to refetch fresh data
      queryClient.invalidateQueries({ queryKey: [{ _id: 'getTutorCertifications' }] });
    },
  });
}
